'use client';
import React from "react";
import Link from "next/link";
import { motion } from "framer-motion";
import { useConvexAuth } from "convex/react";
import { useBoard } from "@/components/BoardProvider";
import { useAuthModal } from "@/auth/auth-modal-provider";
import {
  FileText,
  BookOpen,
  Calculator,
  BookText,
  BrainCircuit,
  Lightbulb,
  ChevronRight,
} from "lucide-react";

type Feature = {
  icon: React.ElementType;
  eyebrow: string;
  title: string;
  description: string;
  /**
   * Basename of the per-board screen-recording. Resolved at render to
   * `/features/{board.slug}/{clip}.mp4`, so each board serves its own set
   * (e.g. /features/tmua/exams.mp4, /features/esat/exams.mp4).
   */
  clip: string;
  /**
   * Per-feature call to action. `requiresAuth` destinations sit behind the
   * dashboard, so signed-out visitors get the signup modal instead of a
   * redirect they can't complete.
   */
  cta: { label: string; href: string; requiresAuth?: boolean };
};

const FEATURES: Feature[] = [
  {
    icon: FileText,
    eyebrow: "Mock exams",
    title: "Sit the real thing, before the real thing",
    description:
      "Timed mocks in the Pearson VUE interface, marked against the official scale.",
    clip: "exams",
    cta: { label: "Sit a mock exam", href: "/dashboard/exams", requiresAuth: true },
  },
  {
    icon: BookOpen,
    eyebrow: "Question bank",
    title: "Every past-paper question, one search away",
    description:
      "Filter the entire syllabus by topic and drill exactly what you're weak on, with instant feedback or full mock mode.",
    clip: "question-bank",
    cta: { label: "Explore the question bank", href: "/dashboard/questions", requiresAuth: true },
  },
  {
    icon: BrainCircuit,
    eyebrow: "Adaptive practice",
    title: "Practice that targets your weak spots",
    description:
      "Auto-generated packs that zero in on the topics costing you marks and evolve as your performance changes.",
    clip: "adaptive",
    cta: { label: "Start adaptive practice", href: "/dashboard/practice/adaptive", requiresAuth: true },
  },
  {
    icon: BookText,
    eyebrow: "Textbook",
    title: "Notes written for sixth-formers, not mathematicians",
    description:
      "Concise, exam-focused explanations for every specification point, with worked examples and the official examiner diagrams.",
    clip: "textbook",
    cta: { label: "Read the textbook", href: "/textbook" },
  },
  {
    icon: Lightbulb,
    eyebrow: "Question of the day",
    title: "A new question every single morning",
    description:
      "One hand-picked question a day, with a full worked solution, to build the daily habit and keep your reasoning sharp.",
    clip: "qotd",
    cta: { label: "See today's question", href: "/dashboard/qotd", requiresAuth: true },
  },
  {
    icon: Calculator,
    eyebrow: "Maths dojo",
    title: "Build the speed the clock demands",
    description:
      "Fast, focused mental-maths drills that turn slow arithmetic into instinct, so you spend exam time thinking, not calculating.",
    clip: "dojo",
    cta: { label: "Enter the dojo", href: "/dashboard/dojo", requiresAuth: true },
  },
];

function FeatureMedia({
  src,
  icon: Icon,
}: {
  src: string;
  icon: React.ElementType;
}) {
  const [failed, setFailed] = React.useState(false);

  return (
    <div className="overflow-hidden rounded-lg border border-[var(--border)] bg-[var(--card)] shadow-xl shadow-black/5 ring-1 ring-black/[0.02]">
      {failed ? (
        // Board-agnostic empty state until the recording is added — no board
        // branding, just the feature's own icon on a neutral panel.
        <div className="flex aspect-video w-full items-center justify-center bg-[var(--muted)]/20">
          <Icon className="h-12 w-12 text-[var(--muted-foreground)]/20" strokeWidth={1.25} />
        </div>
      ) : (
        <video
          src={src}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          onError={() => setFailed(true)}
          className="block h-auto w-full"
        />
      )}
    </div>
  );
}

function FeatureCta({ cta }: { cta: Feature["cta"] }) {
  const { openAuthModal } = useAuthModal();
  const { isAuthenticated } = useConvexAuth();

  const className =
    "mt-6 inline-flex items-center gap-1.5 rounded-full bg-primary px-5 py-2.5 font-polysans text-sm font-medium text-white shadow-lg transition-all hover:-translate-y-0.5 hover:bg-primary/90 hover:shadow-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70";

  if (cta.requiresAuth && !isAuthenticated) {
    return (
      <button type="button" onClick={() => openAuthModal("signup")} className={className}>
        {cta.label}
        <ChevronRight className="h-4 w-4" />
      </button>
    );
  }

  return (
    <Link href={cta.href} className={className}>
      {cta.label}
      <ChevronRight className="h-4 w-4" />
    </Link>
  );
}

export function FeaturesSection() {
  const board = useBoard();

  // The dojo is a maths speed game on TMUA; ESAT is a science + maths test, so
  // its dojo copy must not be maths-specific.
  const features =
    board.id === "ESAT"
      ? FEATURES.map((f) =>
          f.clip === "dojo"
            ? {
                ...f,
                eyebrow: "Speed dojo",
                description:
                  "Fast, focused speed drills that turn slow mental calculation into instinct, so you spend exam time thinking, not working it out.",
              }
            : f,
        )
      : FEATURES;

  const variants = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <section className="w-full pt-8 md:pt-16 pb-1 md:pb-2">
      {/* Heading */}
      <div className="max-w-6xl mx-auto px-6 mb-4">
        <h2 className="text-center text-3xl md:text-5xl font-script text-[var(--foreground)] py-2">
          Your entire {board.shortName} prep, in one place
        </h2>
      </div>

      {/* Feature rows */}
      <div className="max-w-6xl mx-auto px-6 pt-10 md:pt-16 space-y-20 md:space-y-28">
        {features.map(({ icon: Icon, eyebrow, title, description, clip, cta }, idx) => {
          const reverse = idx % 2 === 1;
          return (
            <motion.div
              key={idx}
              variants={variants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.55, ease: [0.23, 1, 0.32, 1] }}
              className={`flex flex-col items-center gap-10 md:gap-16 ${
                reverse ? "md:flex-row-reverse" : "md:flex-row"
              }`}
            >
              {/* Media */}
              <div className="w-full md:w-[56%]">
                <FeatureMedia
                  src={`/features/${board.slug}/${clip}.mp4`}
                  icon={Icon}
                />
              </div>

              {/* Text */}
              <div className="w-full md:flex-1 text-center md:text-left">
                <div className="inline-flex items-center gap-2 text-primary">
                  <Icon className="h-4 w-4" />
                  <span className="font-polysans text-sm font-semibold uppercase tracking-wider">
                    {eyebrow}
                  </span>
                </div>
                <h3 className="mt-3 text-2xl md:text-3xl font-semibold text-[var(--foreground)] font-polysans leading-snug">
                  {title}
                </h3>
                <p className="mt-4 text-[var(--muted-foreground)] leading-relaxed font-polysans text-base md:text-lg max-w-md mx-auto md:mx-0">
                  {description}
                </p>
                <FeatureCta cta={cta} />
              </div>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}
